import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edge-functions';
import { toast } from '@/hooks/use-toast';
import { CURRENT_SEASON } from '@/lib/season';
import { useLeagueConfig } from '@/hooks/useLeagueConfig';

export const useSyncFixturesWithOdds = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const { config: leagueConfig, competition } = useLeagueConfig();

  const syncFixturesWithOdds = async (season: string = CURRENT_SEASON, matchday: number = 1) => {
    setIsLoading(true);
    const startTime = Date.now();
    const competitionId = leagueConfig.api_football_league_id;

    try {
      const { data, error } = await invokeEdgeFunction('fetch-fixtures-with-odds', {
        body: { season, matchday, competitionId }
      });

      if (error) {
        console.error('Error syncing fixtures with odds:', error);
        throw new Error(error.message);
      }

      const fixturesCount = data?.totalFixtures || 0;
      const oddsCount = data?.fixturesWithOdds ?? fixturesCount;
      const syncErrors = data?.errors || null;
      console.log(`Synced ${fixturesCount} fixtures for ${competition.name} — ${oddsCount} have odds`);
      if (syncErrors) {
        console.warn('Sync completed with errors:', syncErrors);
      }

      await supabase.from('sync_logs').insert({
        sync_type: 'fixtures',
        season,
        matchdays_synced: [matchday],
        fixtures_updated: fixturesCount,
        results_updated: 0,
        selections_processed: 0,
        errors: syncErrors,
        duration_ms: Date.now() - startTime,
        created_at: new Date().toISOString(),
      }).then(({ error: logErr }) => {
        if (logErr) console.log('sync_logs insert skipped:', logErr.message);
      });

      return data;

    } catch (error) {
      console.error('Error in sync operation:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const syncAllFixtures = async (season: string = CURRENT_SEASON) => {
    setIsSyncingAll(true);
    const competitionId = leagueConfig.api_football_league_id;

    try {
      toast({
        title: "Syncing All Fixtures",
        description: `Fetching all ${competition.name} fixtures for ${season} season. This may take a moment...`,
      });

      const { data, error } = await invokeEdgeFunction('sync-all-fixtures', {
        body: { season, competitionId }
      });

      if (error) {
        console.error('Error syncing all fixtures:', error);
        toast({
          title: "Sync Failed",
          description: error.message || "Failed to sync fixtures",
          variant: "destructive"
        });
        throw error;
      }

      toast({
        title: "Sync Complete",
        description: `Successfully synced ${data?.totalProcessed || 0} ${competition.name} fixtures for ${season} season.`,
      });

      console.log(`Successfully synced ${data?.totalProcessed || 0} fixtures`);
      return data;

    } catch (error) {
      console.error('Error in sync all operation:', error);
      throw error;
    } finally {
      setIsSyncingAll(false);
    }
  };

  return {
    syncFixturesWithOdds,
    syncAllFixtures,
    isLoading,
    isSyncingAll,
    competition,
  };
};
